import type { Dispatch, RefObject } from 'react';

import type { GenericTableData, TableScrollAction, TableSelectionAction } from './types';

// Select logic
export const handleSelectOne = <T extends GenericTableData>(
  event: React.ChangeEvent<HTMLInputElement>,
  item: T,
  allItemIds: number[],
  dispatch: Dispatch<TableSelectionAction<T>>
) => {
  if (event.target.checked) {
    dispatch({ type: 'ADD_SELECTED_ITEM', payload: item });
  } else {
    dispatch({
      type: 'REMOVE_SELECTED_ITEM',
      payload: item.id
    });
  }
};

export const handleSelectAllOnPage = <T extends GenericTableData>(
  event: React.ChangeEvent<HTMLInputElement>,
  allItems: T[],
  dispatch: Dispatch<TableSelectionAction<T>>
) => {
  if (event.target.checked) {
    dispatch({ type: 'ADD_ALL_PAGE_ITEMS', payload: allItems });
  } else {
    dispatch({ type: 'REMOVE_ALL_PAGE_ITEMS', payload: allItems.map((i) => i.id) });
  }
};

// Scroll logic

export const handleScroll = (
  tableRef: RefObject<HTMLElement>,
  dispatch: Dispatch<TableScrollAction>
) => {
  if (!tableRef.current) return;
  const { scrollLeft } = tableRef.current;
  const { scrollWidth } = tableRef.current;
  const { clientWidth } = tableRef.current;
  const maxScroll = scrollWidth - clientWidth;
  const threshold = 10;

  if (scrollLeft < threshold) {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [false, true] });
  } else if (scrollLeft > maxScroll - threshold) {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [true, false] });
  } else {
    dispatch({ type: 'SET_CAN_SCROLL', payload: [true, true] });
  }
};

export const handleScrollLeft = (tableRef: RefObject<HTMLElement>) => {
  if (!tableRef.current) return;
  const currentScroll = tableRef.current.scrollLeft;
  const newScrollLeft = currentScroll > 360 ? currentScroll - 300 : 0;
  tableRef.current.scrollTo({
    left: newScrollLeft,
    behavior: 'smooth'
  });
};

export const handleScrollRight = (tableRef: RefObject<HTMLElement>) => {
  if (!tableRef.current) return;
  const maxScroll = tableRef.current.scrollWidth - tableRef.current.clientWidth;
  const currentScroll = tableRef.current.scrollLeft;
  const newScrollLeft = maxScroll > 360 ? currentScroll + 300 : maxScroll;
  tableRef.current.scrollTo({
    left: newScrollLeft,
    behavior: 'smooth'
  });
};

export const isScrollable = (element: HTMLElement) =>
  element && element.scrollWidth > element.clientWidth + 20;

export const handleScrollableEnter = (
  tableRef: RefObject<HTMLElement>,
  dispatch: Dispatch<TableScrollAction>
) => {
  if (tableRef.current && isScrollable(tableRef.current)) {
    dispatch({ type: 'SET_TABLE_HOVERED', payload: true });
    handleScroll(tableRef, dispatch);
  }
};

export const handleScrollableExit = (dispatch: Dispatch<TableScrollAction>) => {
  dispatch({ type: 'SET_TABLE_HOVERED', payload: false });
};

// Visual logic

export const tablePadding = (density: string) => {
  if (density === 'Default') {
    return '22px';
  }
  if (density === 'Compact') {
    return '15px';
  }
  return '8px';
};

// Reusable table selection state logic

export const removeSelected = <T extends GenericTableData>(
  selectedItems: T[],
  selectedItemIds: number[],
  itemId: number
) => {
  const newSelectedItemIds = selectedItemIds.filter((id) => id !== itemId);
  const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const addSelected = <T extends GenericTableData>(
  selectedItems: T[],
  selectedItemIds: number[],
  item: T
) => {
  const newSelectedItemIds = [...selectedItemIds, item.id];
  const newSelectedItems = [...selectedItems, item];
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const addAllPageItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: T[]
) => {
  const newSelectedItems = Array.from(new Set([...selectedItems, ...itemsOnPage]));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const removeAllPageItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: number[]
) => {
  const newSelectedItems = selectedItems.filter((item) => !itemsOnPage.includes(item.id));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};

export const removeAllSelectedItems = <T extends GenericTableData>(
  selectedItems: T[],
  itemsOnPage: number[]
) => {
  const newSelectedItems = selectedItems.filter((item) => !itemsOnPage.includes(item.id));
  const newSelectedItemIds = newSelectedItems.map((item) => item.id);
  return {
    selectedItemIds: newSelectedItemIds,
    selectedItems: newSelectedItems
  };
};
